import { NavigationProp, ParamListBase } from "@react-navigation/core";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import React from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewProps,
} from "react-native";
import { useSelector } from "react-redux";
import moment from "moment";

import { tOrderResponse } from "tmt_api/src/types/types.validator";
import * as mappers from "tmt_api/src/shared/mappers";

import * as utilsDb from "src/db/utils.db";
import * as orderDb from "src/db/order.db";
import * as connectionDb from "src/db/connection.db";
import { postReorder } from "src/util/apiNew";
import { PdCopy, PdExport, PdInfo, PdPencilSquare, PdSettings } from "src/components/Icons";
import { PdStack, PdText } from "src/components/layout";
import { useDebounce, useIsMobile, useUserProfile } from "src/hooks/ui";
import { formatDate, formatMoney, formatPercentage } from "src/util/gFormatting";
import { tColumn, tRowInstance, tTableSortDirection, useTable } from "src/hooks/table";
import { getColorSchemeFromOrderStatus } from "src/util/ui";
import { tPressableState } from "src/components/types";
import { kRoute } from "src/kRoute";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { PdButton, PdButtonLabel, tPdButtonProps } from "src/components/v2/forms/button";
import { isApiError } from "src/util/apiNew";
import { AlertModal, tAlertModalProps } from "src/components/Modals/AlertModal";
import { useModal } from "src/providers/OverlayProvider";
import { PdSelect, tPdSelectOption } from "src/components/forms/select";
import {
  PdBadge,
  PdBadgeLabel,
  PdTab,
  PdTabLabel,
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTableMobileItemSeparator,
  PdTablePaginationFooter,
  PdTableRow,
  PdTableRowMobile,
  PdTableRowMobileButton,
  PdTableRowMobileLabel,
  PdTableRowMobilePanel,
  PdTableRowMobileValue,
  PdTableSortableHeaders,
  tPdTable,
} from "src/components/v2/layout";
import { useExportOrdersModal } from "src/hooks/modals";
import { useAppDispatch } from "src/redux";
import { generateCSVFromTableInstance, getHiddenColumnsFromConfig } from "src/util/table";
import { kTablePageSize } from "src/util/constants";
import {
  changeFilterNextPage,
  changeFilterOrderStatus,
  changeFilterPrevPage,
  changeFilterTerm,
  resetFilters,
} from "src/redux/filters/actions";
import { PdSearchInput } from "src/components/v2/ui";
import { PdFormLabel } from "src/components/v2/forms/control";
import { PdPopover, PdPopoverContent, PdPopoverTrigger } from "src/components/v2/overlay";
import { PdTooltip } from "src/components/overlay";
import PopoverContentTableColumns from "src/components/connected/PopoverContentTableColumns";
import { PdRow } from "src/components/v2/ui/core";

type tTableState = {
  data: tOrderResponse[];
  isLoading: boolean;
  prev: string | null;
  next: string | null;
};

const BRAND_HIDDEN_COLUMNS = [
  "reorder-button",
  "retailerAccountManagerName",
  "brandName",
  "shipping",
  "repCommissionPercent",
  "creditUsedAmount",
  "isConverted",
];
const REP_HIDDEN_COLUMNS = [
  "reorder-button",
  "retailerAccountManagerName",
  "repName",
  "tmtCommissionFee",
  "serviceFee",
  "creditUsedAmount",
  "isConverted",
];
const RETAILER_HIDDEN_COLUMNS = [
  "retailerAccountManagerName",
  "retailerName",
  "source",
  "repCommissionPercent",
  "repCommissionEarned",
  "tmtCommissionFee",
  "serviceFee",
  "isConverted",
];
const ADMIN_HIDDEN_COLUMNS = ["reorder-button"];

const sortByMobileOptions = [
  { label: "Most recent", value: "orderDate-desc" },
  { label: "Oldest", value: "orderDate-asc" },
];

const formatOrderMobileDate = (dateInMilliseconds: number) => moment(dateInMilliseconds).format("MMM DD, YYYY");

const CreateAnOrderButtonIcon: React.FC<ViewProps> = ({ style, ...rest }) => {
  return (
    <View style={[styles.createOrderButtonIconContainer, style]} {...rest}>
      <PdPencilSquare color={kColors.white} style={styles.createOrderButtonIcon} />
    </View>
  );
};

const CreateAnOrderButton: React.FC<tPdButtonProps> = ({ contentContainerStyle, ...rest }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Pressable {...rest}>
        <CreateAnOrderButtonIcon />
      </Pressable>
    );
  }

  return (
    <View>
      <PdButton
        size="lg"
        colorScheme="pink"
        contentContainerStyle={[styles.createOrderButton, contentContainerStyle]}
        {...rest}
      >
        <CreateAnOrderButtonIcon style={styles.createOrderButtonIconContainerWeb} />
        <PdButtonLabel>Create an Order</PdButtonLabel>
      </PdButton>
    </View>
  );
};

const OrderMobileRow: React.FC<{
  item: tRowInstance<tOrderResponse>;
  onPress?: (item: tRowInstance<tOrderResponse>) => void;
}> = ({ item, onPress }) => {
  const { isBrand, isRep } = useUserProfile();

  const handleOnPress = () => {
    onPress?.(item);
  };

  let title = item.brandName;
  if (isBrand) title = item.retailerName;
  if (isRep) title = `${item.repName} | ${item.brandName}`;

  return (
    <PdTableRowMobile>
      <PdTableRowMobileButton>
        <PdText style={styles.flex1} variant="body-sm">
          {title}
        </PdText>
        <PdBadge style={styles.pdTableRowMobileBadge} colorScheme={getColorSchemeFromOrderStatus(item.status)}>
          <PdBadgeLabel>{item.status}</PdBadgeLabel>
        </PdBadge>
      </PdTableRowMobileButton>
      <PdTableRowMobilePanel>
        <TouchableOpacity style={styles.pdTableRowMobileContent} onPress={handleOnPress}>
          <PdStack gap={1} direction="vertical">
            <PdText variant="body-sm">{formatOrderMobileDate(item.orderDate)}</PdText>
            <PdRow>
              <PdTableRowMobileLabel>Order</PdTableRowMobileLabel>
              <PdTableRowMobileValue>{`#${item.orderNumber}`}</PdTableRowMobileValue>
            </PdRow>
            <PdRow>
              <PdTableRowMobileLabel>Total</PdTableRowMobileLabel>
              <PdTableRowMobileValue>{formatMoney(item.total)}</PdTableRowMobileValue>
            </PdRow>
            <PdRow>
              <PdTableRowMobileLabel>Shipping</PdTableRowMobileLabel>
              <PdTableRowMobileValue>{formatMoney(item.shipping)}</PdTableRowMobileValue>
            </PdRow>
            <PdRow>
              <PdTableRowMobileLabel>Retailer</PdTableRowMobileLabel>
              <PdTableRowMobileValue>{item.retailerName}</PdTableRowMobileValue>
            </PdRow>
            <PdRow>
              <PdTableRowMobileLabel>Sales Rep</PdTableRowMobileLabel>
              <PdTableRowMobileValue>{item.repName}</PdTableRowMobileValue>
            </PdRow>
          </PdStack>
        </TouchableOpacity>
      </PdTableRowMobilePanel>
    </PdTableRowMobile>
  );
};

const OrderRow: React.FC<{
  item: tRowInstance<tOrderResponse>;
  onPress?: (item: tRowInstance<tOrderResponse>) => void;
}> = ({ item, onPress }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <Pressable onPress={handleOnPress}>
      {({ pressed, hovered }: tPressableState) => (
        <PdTableRow isPressed={pressed} isHovered={hovered}>
          {item.cells.map((cell) => (
            <PdTableCell key={cell.uniqueId} style={cell.style}>
              {cell.render("Cell")}
            </PdTableCell>
          ))}
        </PdTableRow>
      )}
    </Pressable>
  );
};
const ReorderCell: React.FC<{ orderId: string; brandName: string; style?: StyleProp<TextStyle> }> = ({
  orderId,
  brandName,
  style,
}) => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const alertModal = useModal<tAlertModalProps>(AlertModal);

  const handleOnPress = async () => {
    try {
      const response = await postReorder(orderId);

      if (response?.message) {
        alertModal.openAsync({ title: "INVENTORY ALERT", description: response.message });
      } else {
        dispatch({ type: "RIBBON", message: "Products added to cart" });
      }
    } catch (error) {
      if (isApiError(error) && error?.response?.data.status === 409) {
        const response = await alertModal.openAsync({
          title: "INVENTORY ALERT",
          description: error.response.data.message,
          buttonLabel: "Shop Brand",
        });
        if (response.status === "closed") return;
        navigation.navigate(kRoute.P100, { brand: brandName });
      } else {
        dispatch({ type: "RIBBON", message: "An error occurred please try again." });
      }
    }
  };

  return (
    <PdButton contentContainerStyle={styles.reorderButton} onPress={handleOnPress} size="sm" style={style}>
      <PdButtonLabel>Reorder</PdButtonLabel>
    </PdButton>
  );
};

const useTableColumnsOrders = () => {
  const userType = useSelector((state) => state.UserReducer.profile.accountType);
  return React.useMemo(() => {
    const columns: tColumn<tOrderResponse>[] = [
      {
        id: "reorder-button",
        header: "Reorder",
        accessor: (originalRow) => originalRow.orderId,
        Cell: ({ value, row, style }) => (
          <ReorderCell orderId={String(value)} brandName={row.brandName} style={style} />
        ),
        style: styles.tableColumnSm,
        isSortable: false,
      },
      {
        id: "retailerAccountManagerName",
        header: "Account Manager",
        accessor: (originalRow) => originalRow.retailerAccountManagerName || "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "orderNumber",
        header: "Order #",
        accessor: (originalRow) => originalRow.orderNumber,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
      {
        id: "orderDate",
        header: "Order Date",
        accessor: (originalRow) => formatDate(originalRow.orderDate),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
      {
        id: "brandName",
        header: "Brand Name",
        accessor: (originalRow) => originalRow.brandName,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnLg,
      },
      {
        id: "retailerName",
        header: "Retailer Name",
        accessor: (originalRow) => originalRow.retailerName,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnLg,
      },
      {
        id: "source",
        header: "Source",
        accessor: (originalRow) => originalRow.source,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "status",
        header: "Order Status",
        accessor: (originalRow) => originalRow.status,
        Cell: ({ value, row }) => (
          <PdBadge colorScheme={getColorSchemeFromOrderStatus(row.status)}>
            <PdBadgeLabel>{value}</PdBadgeLabel>
          </PdBadge>
        ),
        style: styles.tableColumnMd,
      },
      {
        id: "total",
        header: "Order Total",
        accessor: (originalRow) => formatMoney(originalRow.total),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "subtotal",
        header: "Order Subtotal",
        accessor: (originalRow) => formatMoney(originalRow.subtotal),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "shipping",
        header: "Shipping",
        accessor: (originalRow) => formatMoney(originalRow.shipping),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
      {
        id: "promoCode",
        header: "Promotional Code",
        accessor: (originalRow) => originalRow.promoCode,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "discount",
        header: "Discount Amount",
        accessor: (originalRow) => formatMoney(originalRow.discount),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "repCommissionPercent",
        header: "Commission %",
        accessor: (originalRow) =>
          originalRow.repCommissionPercent ? formatPercentage(originalRow.repCommissionPercent) : "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
      {
        id: "repCommissionEarned",
        header: userType === "rep" ? "Commission Earned" : "Sales Rep Commission",
        accessor: (originalRow) => formatMoney(originalRow.repCommissionEarned),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "repName",
        header: "Sales Rep Name",
        accessor: (originalRow) => originalRow.repName,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnLg,
      },
      {
        id: "tmtCommissionFee",
        header: "Main Tab Commissions",
        accessor: (originalRow) => formatMoney(originalRow.tmtCommissionFee),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "serviceFee",
        header: "Service Fee",
        accessor: (originalRow) => formatMoney(originalRow.serviceFee),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
      {
        id: "creditUsedAmount",
        header: "TMT Credit used",
        accessor: (originalRow) => formatMoney(originalRow.creditUsedAmount),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
      {
        id: "isConverted",
        header: "Abandoned Cart Conversion",
        accessor: (originalRow) => (originalRow.isConverted ? "Yes" : "No"),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
    ];
    if (userType === "brand") return columns.filter((column) => !BRAND_HIDDEN_COLUMNS.includes(column.id));
    if (userType === "rep") return columns.filter((column) => !REP_HIDDEN_COLUMNS.includes(column.id));
    if (userType === "retailer") return columns.filter((column) => !RETAILER_HIDDEN_COLUMNS.includes(column.id));
    if (userType === "tmtAdmin") return columns.filter((column) => !ADMIN_HIDDEN_COLUMNS.includes(column.id));
    return columns;
  }, [userType]);
};

const OrdersTable = <T extends unknown>(
  props: React.PropsWithChildren<tPdTable<T> & { isFiltering: boolean; prev: string | null; next: string | null }>,
): React.ReactElement | null => {
  const { data, isFiltering, prev, next, ...rest } = props;
  const { isRetailer, isBrand, isRep } = useUserProfile();
  const filters = useSelector((state) => state.FiltersReducer.orders);
  const rCompany = useSelector(({ CompanyReducer }) => CompanyReducer);
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const dispatch = useAppDispatch();
  const navigation = useNavigation();

  const handleOnPressPrevPage = () => {
    dispatch(changeFilterPrevPage({ pageName: "orders", value: prev ?? "" }));
  };

  const handleOnPressNextPage = () => {
    dispatch(changeFilterNextPage({ pageName: "orders", value: next ?? "" }));
  };

  const handleOnPressMarketplace = () => {
    navigation.navigate(kRoute.P99);
  };

  const handleOnPressWriteAnOrder = async () => {
    if (isBrand && rCompany.type === "brand") {
      navigation.navigate(kRoute.P100, { brand: rCompany.name });
    } else if (isRep) {
      const connectionsQuery = await connectionDb.queryBrandRepConnection({ repId: rCompanyId }).get();
      if (connectionsQuery.size > 1) {
        navigation.navigate(kRoute.P99);
      } else if (connectionsQuery.docs[0]) {
        navigation.navigate(kRoute.P100, { brand: connectionsQuery.docs[0].data().business.name });
      }
    }
  };

  if ((data?.length ?? 0) === 0) {
    if (isFiltering) {
      return (
        <View style={styles.center}>
          <PdText>No results found.</PdText>
        </View>
      );
    }

    if (isRetailer) {
      return (
        <View style={styles.retailersNoResults}>
          <PdText>Browse our brands on the </PdText>
          <TouchableOpacity onPress={handleOnPressMarketplace}>
            <PdText style={styles.textLink}>marketplace</PdText>
          </TouchableOpacity>
          <PdText> and place your first order!</PdText>
        </View>
      );
    }

    if (isBrand || isRep) {
      return (
        <View style={styles.center}>
          <PdText style={styles.mb1}>
            Write & share your first order with a retailer, and as always, they are commission free! To create an order,
            simply add your products to a cart and share the link with a retailer.
          </PdText>
          <TouchableOpacity onPress={handleOnPressWriteAnOrder}>
            <PdText style={styles.textLink}>{`Let's write an order!`}</PdText>
          </TouchableOpacity>
        </View>
      );
    }
  }

  return (
    <>
      <PdTable data={data} {...rest} />
      <PdTablePaginationFooter
        page={filters.page}
        hasNext={!!next}
        onPressBack={handleOnPressPrevPage}
        onPressNext={handleOnPressNextPage}
      />
    </>
  );
};

const P03_AllOrders: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const filters = useSelector((state) => state.FiltersReducer.orders);
  const [tableState, setTableState] = React.useState<tTableState>({
    isLoading: true,
    data: [],
    prev: null,
    next: null,
  });
  const isFocused = useIsFocused();
  const dTermFilter = useDebounce(filters.term, 500);
  const isMobile = useIsMobile();
  const { isBrand, isRep } = useUserProfile();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rAccountType = useSelector((state) => state.UserReducer.profile.accountType);
  const columns = useTableColumnsOrders();
  const rCompany = useSelector(({ CompanyReducer }) => CompanyReducer);
  const rTableConfigs = useSelector((state) => state.TableConfigReducer.orders);
  const tableInstance = useTable<tOrderResponse>({
    data: tableState.data,
    columns,
    initialState: {
      sortedBy: "orderDate",
      sortDirection: "desc",
      hiddenColumns: getHiddenColumnsFromConfig(rTableConfigs),
    },
  });
  const dispatch = useAppDispatch();
  const exportOrdersModal = useExportOrdersModal();

  const isFiltering = dTermFilter !== "" || !!filters.orderStatus;

  const sortedByValue = React.useMemo(
    () =>
      sortByMobileOptions.find(
        (option) => option.value === `${tableInstance.state.sortedBy}-${tableInstance.state.sortDirection}`,
      ),
    [tableInstance.state.sortDirection, tableInstance.state.sortedBy],
  );

  const handleOnCreateOrder = async () => {
    if (isBrand && rCompany.type === "brand") {
      navigation.navigate(kRoute.P100, { brand: rCompany.name });
    } else if (isRep) {
      const connectionsQuery = await connectionDb.queryBrandRepConnection({ repId: rCompanyId }).get();
      if (connectionsQuery.size > 1) {
        navigation.navigate(kRoute.P99);
      } else if (connectionsQuery.docs[0]) {
        navigation.navigate(kRoute.P100, { brand: connectionsQuery.docs[0].data().business.name });
      }
    }
  };

  const handleOnExportOrders = () => {
    exportOrdersModal.openAsync();
  };

  const handleOnPressCopyAsCSV = async () => {
    try {
      await navigator.clipboard.writeText(generateCSVFromTableInstance(tableInstance.rows, tableInstance.headers));
      dispatch({ type: "RIBBON", message: "Copied to clipboard" });
    } catch (er) {
      dispatch({ type: "RIBBON", message: new Error("Could not copy to clipboard") });
    }
  };

  const handleOnSelectSortBy = (option: tPdSelectOption | null) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const sortValues = option?.value.split("-") as [keyof tOrderResponse, tTableSortDirection];
    const sortedBy = sortValues[0];
    const sortDirection = sortValues[1];
    tableInstance.sort(sortedBy, sortDirection);
  };

  const navigateToOrderView = React.useCallback(
    (row: tRowInstance<tOrderResponse>) => {
      navigation.navigate(kRoute.P03_01, { id: row.orderId });
    },
    [navigation],
  );

  const renderOrderItem: React.FC<{ item: tRowInstance<tOrderResponse> }> = React.useCallback(
    ({ item }) => {
      if (isMobile) return <OrderMobileRow item={item} onPress={navigateToOrderView} />;
      return <OrderRow item={item} onPress={navigateToOrderView} />;
    },
    [isMobile, navigateToOrderView],
  );

  const updateHiddenColumns = (newTableConfig: Record<keyof tOrderResponse, boolean>) => {
    const newHiddenColumns = getHiddenColumnsFromConfig(newTableConfig);
    tableInstance.setHiddenColumns(newHiddenColumns);
  };

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const params: orderDb.tQueryOrdersParams = { term: dTermFilter, status: filters.orderStatus };
      if (rAccountType === "brand") params.brandId = rCompanyId;
      if (rAccountType === "retailer") params.retailerId = rCompanyId;
      if (rAccountType === "rep") params.repId = rCompanyId;
      const query = await utilsDb.withPagination(orderDb.queryOrder, orderDb.documentOrder, {
        ...params,
        prev: filters.prev,
        next: filters.next,
        limit: kTablePageSize,
      });

      unsubscribe = query.onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => mappers.mapOrder(doc.data(), rAccountType));
        setTableState({
          data,
          isLoading: false,
          prev: data[0]?.orderId ?? "",
          next: data.length >= kTablePageSize ? data[data.length - 1]?.orderId ?? "" : "",
        });
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [dTermFilter, filters.next, filters.orderStatus, filters.prev, rAccountType, rCompanyId]);

  React.useEffect(() => {
    if (!isFocused) {
      dispatch(resetFilters());
    }
  }, [dispatch, isFocused]);

  if (tableState.isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={kColors.brandGreen} />
      </View>
    );
  }

  return (
    <View style={isMobile ? styles.mobilePage : styles.page}>
      <View style={styles.headerContainer}>
        <PdText variant="subheader">Orders</PdText>
        {(isBrand || isRep) && (
          <View style={styles.createOrderButtonContainer}>
            <CreateAnOrderButton onPress={handleOnCreateOrder} />
            {!isMobile && (
              <PdTooltip
                direction="top-left"
                render={() => (
                  <PdText style={styles.tooltipText}>
                    To create orders, simply shop the brand and add to cart. Then share the link with your Retailers on
                    The Main Tab!
                  </PdText>
                )}
              >
                <View style={styles.infoIcon}>
                  <PdInfo color={kColors.brandDark} />
                </View>
              </PdTooltip>
            )}
          </View>
        )}
      </View>
      <View style={styles.mb4}>
        {!isMobile && <PdFormLabel>Search your orders</PdFormLabel>}
        <PdSearchInput
          value={filters.term}
          onChangeText={(value) => dispatch(changeFilterTerm({ pageName: "orders", value }))}
          placeholder="Search Orders"
        />
      </View>
      <View style={styles.filtersContainer}>
        <View style={styles.filtersStatusContainer}>
          <PdTab
            style={{ padding: kStyleConst.kSpacing / 2 }}
            isActive={!filters.orderStatus}
            onPress={() => dispatch(changeFilterOrderStatus({ pageName: "orders" }))}
          >
            <PdTabLabel>All</PdTabLabel>
          </PdTab>
          <PdTab
            style={{ padding: kStyleConst.kSpacing / 2 }}
            isActive={filters.orderStatus === "Pending"}
            onPress={() => dispatch(changeFilterOrderStatus({ pageName: "orders", value: "Pending" }))}
          >
            <PdTabLabel>Pending</PdTabLabel>
          </PdTab>
          <PdTab
            style={{ padding: kStyleConst.kSpacing / 2 }}
            isActive={filters.orderStatus === "Awaiting Fulfillment"}
            onPress={() => dispatch(changeFilterOrderStatus({ pageName: "orders", value: "Awaiting Fulfillment" }))}
          >
            <PdTabLabel>Awaiting Fulfillment</PdTabLabel>
          </PdTab>
          <PdTab
            style={{ padding: kStyleConst.kSpacing / 2 }}
            isActive={filters.orderStatus === "Shipped"}
            onPress={() => dispatch(changeFilterOrderStatus({ pageName: "orders", value: "Shipped" }))}
          >
            <PdTabLabel>Shipped</PdTabLabel>
          </PdTab>
          <PdTab
            style={{ padding: kStyleConst.kSpacing / 2 }}
            isActive={filters.orderStatus === "Declined"}
            onPress={() => dispatch(changeFilterOrderStatus({ pageName: "orders", value: "Declined" }))}
          >
            <PdTabLabel>Declined</PdTabLabel>
          </PdTab>
        </View>
        {!isMobile && (
          <PdStack style={styles.mlAuto}>
            <PdButton
              colorScheme="pink"
              size="sm"
              rightIcon={<PdExport style={styles.iconSm} />}
              onPress={handleOnExportOrders}
            >
              <PdButtonLabel>Export</PdButtonLabel>
            </PdButton>
            <PdButton
              colorScheme="gray"
              size="sm"
              rightIcon={<PdCopy style={styles.iconSm} />}
              onPress={handleOnPressCopyAsCSV}
            >
              <PdButtonLabel>Copy as CSV</PdButtonLabel>
            </PdButton>
            <PdPopover offset={22 + 2 * kStyleConst.kSpacing}>
              <PdPopoverTrigger>
                <PdButton colorScheme="gray" size="sm" rightIcon={<PdSettings style={styles.iconSm} />}>
                  <PdButtonLabel>Configure Table</PdButtonLabel>
                </PdButton>
              </PdPopoverTrigger>
              <PdPopoverContent style={styles.configureTablePopoverContent}>
                <PopoverContentTableColumns
                  columns={columns}
                  tableConfigReduxKey="orders"
                  onPressCheckbox={updateHiddenColumns}
                />
              </PdPopoverContent>
            </PdPopover>
          </PdStack>
        )}
      </View>
      {isMobile && (
        <PdSelect
          containerStyle={styles.sortByComboBox}
          value={sortedByValue}
          onChange={handleOnSelectSortBy}
          options={sortByMobileOptions}
          isClearable={false}
        />
      )}
      <OrdersTable
        ListHeaderComponent={!isMobile ? <PdTableSortableHeaders headers={tableInstance.headers} /> : null}
        ItemSeparatorComponent={() => (isMobile ? <PdTableMobileItemSeparator /> : null)}
        data={tableInstance.rows}
        keyExtractor={(item) => item.orderId}
        renderItem={renderOrderItem}
        key={`${isMobile}`}
        style={isMobile ? styles.mobileTable : {}}
        isFiltering={isFiltering}
        isMobile={isMobile}
        prev={tableState.prev}
        next={tableState.next}
      />
    </View>
  );
};

export const P03 = React.memo(P03_AllOrders);

const styles = StyleSheet.create({
  center: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  configureTablePopoverContent: {
    backgroundColor: kColors.white,
    borderColor: kColors.gray300,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 1,
    left: -150,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 3,
    right: 0,
  },
  createOrderButton: {
    borderRadius: 999,
    paddingRight: kStyleConst.kSpacing * 8,
    right: 10,
  },
  createOrderButtonContainer: {
    alignItems: "center",
    flexDirection: "row",
  },
  createOrderButtonIcon: {
    height: 20,
    marginLeft: 2,
    width: 20,
  },
  createOrderButtonIconContainer: {
    alignItems: "center",
    backgroundColor: kColors.brand500,
    borderRadius: 999,
    height: 42,
    justifyContent: "center",
    width: 42,
  },
  createOrderButtonIconContainerWeb: {
    position: "absolute",
    right: -10,
  },
  filtersContainer: {
    alignItems: "flex-end",
    backgroundColor: kColors.white,
    flexDirection: "row",
    marginBottom: 2 * kStyleConst.kSpacing,
    width: "100%",
    zIndex: 1,
  },
  filtersStatusContainer: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginRight: 2 * kStyleConst.kSpacing,
  },
  flex1: {
    flex: 1,
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: kStyleConst.kSpacing * 4,
  },
  iconSm: {
    height: 11,
    width: 11,
  },
  infoIcon: {
    height: 22,
    marginLeft: 3 * kStyleConst.kSpacing,
    width: 22,
  },
  mb1: {
    marginBottom: kStyleConst.kSpacing * 1,
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mobilePage: {
    flex: 1,
    paddingBottom: kStyleConst.kSpacing * 2,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingTop: kStyleConst.kSpacing * 4,
  },
  mobileTable: {
    marginHorizontal: -(kStyleConst.kSpacing * 4),
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 2,
  },
  page: {
    flex: 1,
    paddingBottom: kStyleConst.kSpacing * 4,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingTop: kStyleConst.kSpacing * 8,
  },
  pdTableRowMobileBadge: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  pdTableRowMobileContent: {
    padding: kStyleConst.kSpacing * 2,
    paddingBottom: kStyleConst.kSpacing * 3,
    paddingLeft: 45,
  },
  reorderButton: {
    marginRight: "auto",
  },
  retailersNoResults: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
  },
  sortByComboBox: {
    zIndex: 1,
  },
  tableColumnLg: {
    width: 300,
  },
  tableColumnMd: {
    width: 200,
  },
  tableColumnSm: {
    width: 120,
  },
  textLink: {
    fontWeight: "500",
  },
  tooltipText: {
    color: kColors.white,
    fontSize: 11,
    fontWeight: "300",
    letterSpacing: 1.2,
    lineHeight: 19,
  },
});
